import React from 'react';
import { loadCalendarMonth } from '../utils/hinduCalendarCache';
import { Box, Card, CardContent, CircularProgress, Container, IconButton, Stack } from '@mui/material';
import { CalendarMonth, ChevronLeft, ChevronRight } from '@mui/icons-material';
import HinduCalendarSeo from '../components/seo/HinduCalendarSeo';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CitySearch from '../components/forms/CitySearch';
import Typography from '../components/i18n/LocalizedTypography';
import { LocalizedAlert as Alert, LocalizedButton as Button } from '../components/i18n/LocalizedMui';
import { inlineLocale } from '../i18n/inlineTranslations';
import { hinduCalendarDateLabel, hinduCalendarFestivalName, hinduCalendarLunarDay, hinduCalendarMonthTitle, hinduCalendarText, hinduCalendarWeekdays } from '../i18n/hinduCalendarTranslations';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const ICONS: Record<string, string> = {
  new_moon: '🌑', full_moon: '🌕', vrat: '🙏', sun: '☀️', diya: '🪔',
  shiva: '🔱', ganesha: '🐘', flag: '🇮🇳', kalash: '🪷',
};

interface City {
  id: string;
  name: string;
  displayName: string;
  coordinates: { latitude: number; longitude: number };
  timezone: { name: string; offset: number };
  population: number;
  admin1?: { name: string; code?: string };
  country?: { name: string; code: string };
}

export interface HinduCalendarFestival {
  id: string;
  name: string;
  nameLocalized?: Record<string, string>;
  icon: string;
  isMajor: boolean;
  spansDates: boolean;
  dateStart: string;
  dateEnd: string;
  type?: string;
  regions?: string[];
  tags?: string[];
  timing?: { type: string; start: string; end: string } | null;
}

export interface HinduCalendarDayData {
  date: string;
  gregorianDay: number;
  lunarDayNumber: number;
  lunarDayLabel: string;
  isPurnima: boolean;
  isAmavasya: boolean;
  hasMajorFestival: boolean;
  festivals: HinduCalendarFestival[];
  panchang: any;
}

const DEFAULT_CITY: City = {
  id: 'new-delhi-default', name: 'New Delhi', displayName: 'New Delhi, India',
  coordinates: { latitude: 28.6139, longitude: 77.209 },
  timezone: { name: 'Asia/Kolkata', offset: 330 }, population: 0,
  country: { name: 'India', code: 'IN' },
};

const monthKey = (date: Date) => ({ year: date.getFullYear(), month: date.getMonth() + 1 });
const dateKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const isSameMonth = (left: Date, right: Date) => left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();
const initialMonth = (params: URLSearchParams) => {
  const year = Number(params.get('year'));
  const month = Number(params.get('month'));
  return Number.isInteger(year) && year >= 1900 && year <= 2200 && Number.isInteger(month) && month >= 1 && month <= 12
    ? new Date(year, month - 1, 1)
    : new Date();
};
const initialCity = (params: URLSearchParams): City | undefined => {
  const latitudeParam = params.get('lat');
  const longitudeParam = params.get('lon');
  const timezoneOffsetParam = params.get('timezoneOffset');
  if (latitudeParam === null || longitudeParam === null) return undefined;
  const latitude = Number(latitudeParam);
  const longitude = Number(longitudeParam);
  const timezoneOffset = timezoneOffsetParam === null ? Number.NaN : Number(timezoneOffsetParam);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
  const location = params.get('location') || DEFAULT_CITY.displayName;
  return {
    ...DEFAULT_CITY,
    id: `calendar-${latitude}-${longitude}`,
    name: location.split(',')[0] || location,
    displayName: location,
    coordinates: { latitude, longitude },
    timezone: {
      name: params.get('timezone') || DEFAULT_CITY.timezone.name,
      offset: Number.isFinite(timezoneOffset) ? timezoneOffset : DEFAULT_CITY.timezone.offset,
    },
  };
};
const initialSelectedDate = (params: URLSearchParams, cursor: Date) => {
  const selected = params.get('selectedDate');
  if (selected?.startsWith(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-`)) return selected;
  const today = new Date();
  return isSameMonth(cursor, today) ? dateKey(today) : null;
};
const formatTime = (value?: string, locale = 'en-IN') => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(date);
};
export const festivalsForCalendarDay = (day: HinduCalendarDayData | null | undefined): HinduCalendarFestival[] => {
  const festivals = day?.festivals;
  return Array.isArray(festivals)
    ? festivals.filter((festival): festival is HinduCalendarFestival => Boolean(festival?.id))
    : [];
};
export const hinduCalendarFestivalKey = (festival: HinduCalendarFestival, date: string) => [date, festival.id, festival.dateStart, festival.dateEnd, festival.timing?.start, festival.timing?.end].filter(Boolean).join(':');

const HinduCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || 'en').split(/[-_]/)[0];
  const locale = inlineLocale(language);
  const copy = React.useCallback((key: Parameters<typeof hinduCalendarText>[1]) => hinduCalendarText(language, key), [language]);
  const [cursor, setCursor] = React.useState(() => initialMonth(searchParams));
  const [selectedCity, setCity] = React.useState<City | null | undefined>(() => initialCity(searchParams));
  const city = selectedCity === undefined ? DEFAULT_CITY : selectedCity;
  const [selectedDate, setSelectedDate] = React.useState<string | null>(() => initialSelectedDate(searchParams, initialMonth(searchParams)));
  const [days, setDays] = React.useState<HinduCalendarDayData[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { year, month } = monthKey(cursor);

  React.useEffect(() => {
    if (!city) return;
    const params: Record<string, string> = {
      year: String(year), month: String(month),
      lat: String(city.coordinates.latitude), lon: String(city.coordinates.longitude),
      timezone: city.timezone.name || 'Asia/Kolkata', timezoneOffset: String(city.timezone.offset),
      location: city.displayName || city.name,
    };
    if (selectedDate) params.selectedDate = selectedDate;
    setSearchParams(params, { replace: true });
  }, [city, month, selectedDate, setSearchParams, year]);

  React.useEffect(() => {
    if (!city) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadCalendarMonth(`${API_BASE_URL}/hindu-calendar/month`, { year, month, lat: city.coordinates.latitude, lon: city.coordinates.longitude, timezone: city.timezone.name || 'Asia/Kolkata', timezoneOffset: city.timezone.offset
    }).then((response) => {
      if (!cancelled) {
        const responseDays = response.data?.days;
        setDays(Array.isArray(responseDays) ? responseDays.map((day: HinduCalendarDayData) => ({ ...day, festivals: festivalsForCalendarDay(day) })) : []);
      }
    }).catch((requestError) => {
      if (!cancelled) setError(requestError.response?.data?.error || copy('loadMonthError'));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [city, copy, month, year]);

  const firstWeekday = days.length ? new Date(`${days[0].date}T12:00:00`).getDay() : 0;
  const monthTitle = hinduCalendarMonthTitle(language, cursor, locale);
  const weekdayNames = React.useMemo(() => hinduCalendarWeekdays(language, locale), [language, locale]);
  const festivalName = (festival: HinduCalendarFestival) => hinduCalendarFestivalName(language, festival);
  const selectMonth = (next: Date) => {
    const today = new Date();
    setCursor(next);
    setSelectedDate(isSameMonth(next, today) ? dateKey(today) : null);
  };
  const shiftMonth = (amount: number) => selectMonth(new Date(cursor.getFullYear(), cursor.getMonth() + amount, 1));
  const dateHref = (date: string): string => {
    if (!city) return `/hindu-calendar/${date}`;
    const query = new URLSearchParams({
      lat: String(city.coordinates.latitude), lon: String(city.coordinates.longitude),
      timezone: city.timezone.name || 'Asia/Kolkata', timezoneOffset: String(city.timezone.offset),
      location: city.displayName || city.name,
      viewYear: String(year), viewMonth: String(month),
      selectedDate: date,
    });
    return `/hindu-calendar/${date}?${query.toString()}`;
  };
  const openDate = (date: string, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(dateHref(date), {
      state: { calendarDay: city ? days.find((day) => day.date === date) || null : null },
    });
  };

  return (
    <>
      <HinduCalendarSeo />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Stack spacing={3}>
          <Box textAlign="center">
            <CalendarMonth color="primary" sx={{ fontSize: 42 }} />
            <Typography component="h1" variant="h4" fontWeight={900}>{copy('title')}</Typography>
            <Typography color="text.secondary">{copy('subtitle')}</Typography>
          </Box>

          <Card><CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box flex={1}><CitySearch value={city} onChange={setCity} label={copy('location')} placeholder={copy('searchCity')} required /></Box>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <IconButton onClick={() => shiftMonth(-1)} aria-label={copy('previousMonth')}><ChevronLeft /></IconButton>
                <Typography minWidth={190} textAlign="center" fontWeight={900}>{monthTitle}</Typography>
                <IconButton onClick={() => shiftMonth(1)} aria-label={copy('nextMonth')}><ChevronRight /></IconButton>
                <Button variant="outlined" onClick={() => selectMonth(new Date())}>{copy('today')}</Button>
              </Stack>
            </Stack>
          </CardContent></Card>

          {error && <Alert severity="error">{error}</Alert>}
          {loading ? <Box py={8} textAlign="center"><CircularProgress /><Typography mt={2}>{copy('loadingCalendar')}</Typography></Box> : (
            <Card sx={{ overflow: 'hidden' }}>
              <Box display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))" bgcolor="primary.main" color="primary.contrastText">
                {weekdayNames.map((day) => <Typography key={day} textAlign="center" fontWeight={900} py={1}>{day}</Typography>)}
              </Box>
              <Box display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))">
                {Array.from({ length: firstWeekday }).map((_, index) => <Box key={`blank-${index}`} sx={{ minHeight: { xs: 82, md: 126 }, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }} />)}
                {days.map((day) => {
                  const active = selectedDate === day.date;
                  const festivals = festivalsForCalendarDay(day);
                  return (
                  <Box component="a" href={dateHref(day.date)} key={day.date} onClick={(event: React.MouseEvent<HTMLAnchorElement>) => openDate(day.date, event)}
                    aria-label={`${day.date}: ${festivals.map(festivalName).join(', ') || hinduCalendarLunarDay(language, day.lunarDayLabel)}`}
                    sx={{ minHeight: { xs: 82, md: 126 }, p: { xs: 0.6, md: 1 }, textAlign: 'left', cursor: 'pointer', border: '1px solid', borderColor: active ? 'primary.main' : day.hasMajorFestival ? 'warning.main' : 'divider', borderWidth: active ? 3 : day.hasMajorFestival ? 2 : 1, bgcolor: active ? 'primary.main' : day.hasMajorFestival ? 'rgba(245, 158, 11, 0.12)' : 'background.paper', color: active ? 'primary.contrastText' : 'text.primary', font: 'inherit', textDecoration: 'none', '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover', borderColor: 'primary.main' }, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: -3 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontSize={{ xs: 16, md: 20 }} fontWeight={900}>{day.gregorianDay}</Typography>
                      <Typography component="span" title={day.isPurnima ? copy('purnima') : day.isAmavasya ? copy('amavasya') : undefined}>{day.isPurnima ? '🌕' : day.isAmavasya ? '🌑' : ''}</Typography>
                    </Stack>
                    <Typography fontSize={{ xs: 9, md: 11 }} fontWeight={700} sx={{ opacity: 0.78 }}>{hinduCalendarLunarDay(language, day.lunarDayLabel)}</Typography>
                    <Stack spacing={0.25} mt={0.5}>
                      {festivals.slice(0, 2).map((festival) => <Typography key={hinduCalendarFestivalKey(festival, day.date)} fontSize={{ xs: 9, md: 11 }} fontWeight={festival.isMajor ? 900 : 700} noWrap>{ICONS[festival.icon] || ICONS.kalash} {festivalName(festival)}</Typography>)}
                      {festivals.length > 2 && <Typography fontSize={10}>+{festivals.length - 2} {copy('more')}</Typography>}
                    </Stack>
                  </Box>
                );})}
              </Box>
            </Card>
          )}

          <Card><CardContent>
            <Typography variant="h5" fontWeight={900} mb={2}>{monthTitle} {copy('festivals')}</Typography>
            <Stack spacing={1}>
              {days.flatMap((day) => festivalsForCalendarDay(day).map((festival) => ({ day, festival }))).map(({ day, festival }) => (
                <Box key={hinduCalendarFestivalKey(festival, day.date)} component="a" href={dateHref(day.date)} onClick={(event: React.MouseEvent<HTMLAnchorElement>) => openDate(day.date, event)} sx={{ width: '100%', border: 0, color: 'text.primary', textAlign: 'left', font: 'inherit', textDecoration: 'none', p: 1.25, borderRadius: 2, cursor: 'pointer', bgcolor: festival.isMajor ? 'rgba(245, 158, 11, 0.12)' : 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}>
                  <Typography fontWeight={900}>{ICONS[festival.icon] || ICONS.kalash} {hinduCalendarDateLabel(language, new Date(`${day.date}T12:00:00`), locale)} · {festivalName(festival)}</Typography>
                  {festival.timing && <Typography variant="body2" color="text.secondary">{formatTime(festival.timing.start, locale)}{festival.timing.end !== festival.timing.start ? ` – ${formatTime(festival.timing.end, locale)}` : ''}</Typography>}
                </Box>
              ))}
              {!loading && days.every((day) => festivalsForCalendarDay(day).length === 0) && <Typography color="text.secondary">{copy('emptyMonth')}</Typography>}
            </Stack>
          </CardContent></Card>
        </Stack>
      </Container>
    </>
  );
};

export default HinduCalendar;
